import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { cardRendererFromMetadata } from '@/lib/concept-workbench/card-renderer';
import { mediaService } from '@/services/media/mediaService';

export interface ResolvedMediaUrls {
  /** `media.id` → displayable URL, for every id that has resolved. */
  urls: Record<string, string>;
  /** `media.id` → the card renderer marker its row carries; an id with none is absent. */
  cardRenderers: Record<string, string>;
  /**
   * True while any requested id is still being translated.
   *
   * The canvas cannot tell "this rendition has no image" apart from "the URL for
   * its image has not arrived yet" from the map alone — both read as a missing
   * key. That distinction is the difference between an honest empty state and a
   * surface that tells a user with a finished image that they have none, under a
   * button that spends image quota. So the pending flag is part of the contract,
   * not a detail the caller can derive.
   */
  isResolving: boolean;
}

/**
 * Resolve `media.id` → a displayable URL.
 *
 * The variant set stores media IDs, not URLs, because a rendition must survive
 * the image being re-uploaded or moved between buckets. The canvas needs a URL
 * every frame, so the translation is cached per id and shared across the strip
 * and the stage — four thumbs and one canvas pointing at the same set image
 * resolve once, not five times.
 *
 * `convertToMediaFile` is the existing resolver and is reused rather than
 * re-deriving `getPublicUrl` here: it already handles the case where `url` is a
 * storage path in a non-default bucket, which a hand-rolled copy would get wrong
 * for exactly the generated images this surface deals in.
 */
export function useMediaUrls(mediaIds: readonly (string | null)[]): ResolvedMediaUrls {
  const unique = useMemo(
    () => Array.from(new Set(mediaIds.filter((id): id is string => Boolean(id)))).sort(),
    [mediaIds],
  );

  const results = useQueries({
    queries: unique.map((id) => ({
      queryKey: ['workbench-media-url', id],
      queryFn: async () => {
        const record = await mediaService.getMediaById(id);
        if (!record) return null;
        return {
          url: mediaService.convertToMediaFile(record).url,
          cardRenderer: cardRendererFromMetadata(record.metadata),
        };
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isResolving = results.some((result) => result.isPending);

  return useMemo(() => {
    const map: Record<string, string> = {};
    const renderers: Record<string, string> = {};
    unique.forEach((id, index) => {
      const data = results[index]?.data;
      if (data && typeof data.url === 'string' && data.url.length > 0) map[id] = data.url;
      if (data?.cardRenderer) renderers[id] = data.cardRenderer;
    });
    return { urls: map, cardRenderers: renderers, isResolving };
    // `results` is a new array every render by design; the ids, the resolved
    // URLs and the pending flag are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    unique,
    results.map((r) => `${r.data?.url ?? ''}#${r.data?.cardRenderer ?? ''}`).join('|'),
    isResolving,
  ]);
}
